import { convertirAMXN, formatearMoneda, resolverPago, type CobroTarjetaVista, type Moneda, type VentaVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote, CreditCard, HandCoins, Landmark, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/form';
import { useBarberos, useConfig } from '../agenda/datos';
import { useCobroTarjeta, useEnLinea, useRefrescarCaja, useTerminalesActivas } from './datos';
import { EsperaTerminalDialog } from './EsperaTerminalDialog';
import { imprimirTicket } from './imprimir';
import { avisarError } from '@/lib/avisos';

interface FilaPago {
  metodo: 'efectivo' | 'transferencia' | 'tarjeta';
  moneda: Moneda;
  monto: string;
  referencia: string;
  /** Terminal Point elegida (solo fila tarjeta). */
  terminalId?: string;
}

/** Mismo aviso y ticket para todo cobro terminado (efectivo/transferencia o tarjeta aprobada). */
function avisarCobrada(v: VentaVista) {
  toast.success(`Venta ${v.folio} cobrada${v.cambioMXN ? ` · Cambio ${formatearMoneda(v.cambioMXN)}` : ''}`, {
    action: { label: 'Imprimir ticket', onClick: () => void imprimirTicket(v.id) },
    duration: 10_000,
  });
  void imprimirTicket(v.id).catch(() => undefined);
}

const aCentavos = (texto: string) => Math.round((Number.parseFloat(texto) || 0) * 100);

/** Cobro: pagos mixtos (efectivo MXN/USD, transferencia y tarjeta en terminal Point), propina opcional, cambio en pesos. */
/** Propina de una venta de mostrador aún sin dueño: hay que elegir a quién le toca (o «Para todos»). */
const SIN_ASIGNAR = '__sin_asignar__';
const AVISO_PROPINA = 'Seleccione a quien le pertenece la propina';

export function PanelPago({ venta, bloqueado, onCobrada }: { venta: VentaVista; bloqueado: boolean; onCobrada: () => void }) {
  const tipoCambio = useConfig<number>('caja.tipoCambioUSD', 17);
  const barberos = useBarberos();
  const refrescar = useRefrescarCaja();
  const qc = useQueryClient();
  const enLinea = useEnLinea();
  const terminales = useTerminalesActivas();
  const [esperando, setEsperando] = useState(false);
  const cobroTarjeta = useCobroTarjeta(venta.id, esperando);
  /** Id del cobro cuya resolución se espera: respuestas de otros cobros (viejas) se ignoran. */
  const [cobroEsperadoId, setCobroEsperadoId] = useState<string | null>(null);
  const aprobados = useRef(new Set<string>());
  const [pagos, setPagos] = useState<FilaPago[]>([{ metodo: 'efectivo', moneda: 'MXN', monto: '', referencia: '' }]);
  const [propina, setPropina] = useState({ monto: '', barberoId: venta.barbero?.id ?? SIN_ASIGNAR });

  const propinaMXN = aCentavos(propina.monto);
  const propinaSinDueno = propinaMXN > 0 && propina.barberoId === SIN_ASIGNAR;
  const esCortesia = venta.total === 0 && venta.subtotal > 0 && venta.descuento === venta.subtotal && venta.promocion !== null;
  const ventaEnCeroInvalida = venta.total === 0 && !esCortesia;
  // Caso borde: tarjeta aprobada y registrada sin cerrar la venta (p. ej. turno cerrado). Ya está pagada.
  const tarjetaPreviaMXN = venta.pagos.filter((p) => p.metodo === 'tarjeta').reduce((s, p) => s + p.montoMXN, 0);
  const totalACobrar = Math.max(0, venta.total + propinaMXN - tarjetaPreviaMXN);
  const entrada = pagos
    .filter((p) => aCentavos(p.monto) > 0)
    .map((p) => ({ metodo: p.metodo, moneda: p.moneda, monto: aCentavos(p.monto), tipoCambio: p.moneda === 'USD' ? tipoCambio : 1 }));
  const r = resolverPago(totalACobrar, entrada);
  const filaTarjeta = pagos.find((p) => p.metodo === 'tarjeta');
  const listaTerminales = terminales.data ?? [];
  // Tarjeta solo con conexión, terminales activas y sin una tarjeta ya registrada (TARJETA_UNICA).
  const ofreceTarjeta = enLinea && listaTerminales.length > 0 && tarjetaPreviaMXN === 0;
  const tarjetaLista = !filaTarjeta || (aCentavos(filaTarjeta.monto) > 0 && listaTerminales.some((t) => t.id === filaTarjeta.terminalId && !t.enUso));
  const listo =
    !ventaEnCeroInvalida && r.faltaMXN === 0 && r.valido && tarjetaLista && (totalACobrar === 0 ? entrada.length === 0 : entrada.length > 0);

  const cuerpoPagos = () =>
    pagos
      .filter((p) => aCentavos(p.monto) > 0)
      .map((p) => ({ metodo: p.metodo, moneda: p.moneda, monto: aCentavos(p.monto), referencia: p.referencia || undefined }));
  const cuerpoPropina = () => (propinaMXN > 0 ? { monto: propinaMXN, moneda: 'MXN', barberoId: propina.barberoId || null } : undefined);

  /** Con propina sin dueño no se cobra: se avisa y se queda en el formulario. */
  const siPropinaAsignada = (cobro: () => void) => () => {
    if (propinaSinDueno) toast.error(AVISO_PROPINA);
    else cobro();
  };

  const cobrar = useMutation({
    mutationFn: () => api<VentaVista>(`/ventas/${venta.id}/cobrar`, { method: 'POST', body: { pagos: cuerpoPagos(), propina: cuerpoPropina() } }),
    onSuccess: (v) => {
      avisarCobrada(v);
      refrescar();
      onCobrada();
    },
    onError: (e) => avisarError(e),
  });

  const claveCobro = ['cobro-tarjeta', venta.id];
  const enviarTarjeta = useMutation({
    mutationFn: () =>
      api<CobroTarjetaVista>(`/ventas/${venta.id}/cobrar-tarjeta`, {
        method: 'POST',
        body: { terminalId: filaTarjeta!.terminalId, pagos: cuerpoPagos(), propina: cuerpoPropina() },
      }),
    // Puede volver «pendiente» sin orden en Mercado Pago (fallo de red ambiguo): el servidor reintenta y aquí se sigue esperando.
    onSuccess: async (c) => {
      // Una consulta en vuelo (sondeo o socket) podría pisar el cobro nuevo con el anterior.
      await qc.cancelQueries({ queryKey: claveCobro });
      qc.setQueryData(claveCobro, c);
      setCobroEsperadoId(c.id);
      setEsperando(true);
    },
    onError: (e) => {
      avisarError(e);
      // Si el envío llegó al servidor pese al error, el cobro pendiente reabre la espera.
      void qc.invalidateQueries({ queryKey: claveCobro });
      void qc.invalidateQueries({ queryKey: ['terminales-activas'] });
    },
  });

  // Recarga o cobro iniciado en otra pestaña: con un cobro pendiente se reabre la espera.
  const pendienteId = cobroTarjeta.data?.estado === 'pendiente' ? cobroTarjeta.data.id : null;
  useEffect(() => {
    if (!pendienteId || enviarTarjeta.isPending) return;
    setCobroEsperadoId(pendienteId);
    setEsperando(true);
  }, [pendienteId, enviarTarjeta.isPending]);

  const alAprobar = useCallback(
    async (c: CobroTarjetaVista) => {
      setEsperando(false);
      if (aprobados.current.has(c.id)) return;
      aprobados.current.add(c.id);
      let v: VentaVista | null = null;
      try {
        v = await api<VentaVista>(`/ventas/${venta.id}`);
      } catch {
        // Sin la venta no se sabe si cerró: se refresca y la lista lo dirá.
      }
      if (v && v.estado !== 'pagada') {
        // Caso borde: la tarjeta quedó registrada pero la venta no se cerró (p. ej. caja cerrada).
        toast.warning('Tarjeta aprobada; la venta no se pudo cerrar: cobra el resto');
        setPagos((l) => {
          const resto = l.filter((p) => p.metodo !== 'tarjeta');
          return resto.length ? resto : [{ metodo: 'efectivo', moneda: 'MXN', monto: '', referencia: '' }];
        });
        refrescar();
        return;
      }
      if (v) avisarCobrada(v);
      else toast.success('Cobro con tarjeta aprobado');
      refrescar();
      onCobrada();
    },
    [venta.id, refrescar, onCobrada],
  );
  const alCerrarEspera = useCallback(() => {
    setEsperando(false);
    void qc.invalidateQueries({ queryKey: ['terminales-activas'] });
  }, [qc]);

  const set = (i: number, cambios: Partial<FilaPago>) => setPagos((l) => l.map((p, j) => (j === i ? { ...p, ...cambios } : p)));
  const exacto = (i: number) => {
    const otros = entrada.filter((_, j) => j !== i).reduce((s, p) => s + convertirAMXN(p.monto, p.moneda, p.tipoCambio), 0);
    const falta = Math.max(0, totalACobrar - otros);
    const p = pagos[i]!;
    set(i, { monto: ((p.moneda === 'USD' ? Math.ceil(falta / tipoCambio) : falta) / 100).toFixed(2) });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl bg-primary px-4 py-3 text-on-primary">
        <p className="text-[12px] uppercase tracking-wider opacity-70">Total a cobrar</p>
        <p className="cifras font-display text-[34px] leading-tight">{formatearMoneda(totalACobrar)}</p>
        {propinaMXN > 0 && <p className="cifras text-[12px] opacity-70">Incluye propina de {formatearMoneda(propinaMXN)}</p>}
        {tarjetaPreviaMXN > 0 && (
          <p className="cifras text-[12px] opacity-70">Ya se cobraron {formatearMoneda(tarjetaPreviaMXN)} con tarjeta; falta solo el resto.</p>
        )}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-[13px] font-medium">Pagos</legend>
        {pagos.map((p, i) => (
          <div key={i} className="rounded-lg border border-border p-2">
            <div className="flex gap-1.5">
              <Select
                value={`${p.metodo}-${p.moneda}`}
                onChange={(e) => {
                  const [metodo, moneda] = e.target.value.split('-') as [FilaPago['metodo'], Moneda];
                  const libre = listaTerminales.find((t) => !t.enUso)?.id ?? undefined;
                  set(i, { metodo, moneda, terminalId: metodo === 'tarjeta' ? (p.terminalId ?? libre) : undefined });
                }}
                aria-label="Forma de pago"
                className="w-40"
              >
                <option value="efectivo-MXN">Efectivo MXN</option>
                <option value="efectivo-USD">Efectivo USD</option>
                <option value="transferencia-MXN">Transferencia</option>
                {(ofreceTarjeta || p.metodo === 'tarjeta') && (
                  <option value="tarjeta-MXN" disabled={Boolean(filaTarjeta) && p.metodo !== 'tarjeta'}>
                    Tarjeta
                  </option>
                )}
              </Select>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={p.monto}
                onChange={(e) => set(i, { monto: e.target.value })}
                aria-label="Monto recibido"
                className="cifras flex-1"
                placeholder="0.00"
              />
              {pagos.length > 1 && (
                <Button variante="fantasma" tamano="icono" onClick={() => setPagos((l) => l.filter((_, j) => j !== i))} aria-label="Quitar pago">
                  <Trash2 />
                </Button>
              )}
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <Button variante="enlace" tamano="sm" onClick={() => exacto(i)}>
                Monto exacto
              </Button>
              {p.moneda === 'USD' && aCentavos(p.monto) > 0 && (
                <span className="cifras text-[12px] text-foreground-muted">
                  = {formatearMoneda(convertirAMXN(aCentavos(p.monto), 'USD', tipoCambio))} (TC {tipoCambio})
                </span>
              )}
              {p.metodo === 'tarjeta' && (
                <Select
                  value={p.terminalId ?? ''}
                  onChange={(e) => set(i, { terminalId: e.target.value })}
                  aria-label="Terminal"
                  className="h-8 w-44"
                >
                  {!p.terminalId && <option value="">Elige terminal</option>}
                  {listaTerminales.map((t) => (
                    <option key={t.id} value={t.id ?? ''} disabled={t.enUso}>
                      {`${t.alias ?? t.terminalId} · ${t.enUso ? 'En uso' : 'Libre'}`}
                    </option>
                  ))}
                </Select>
              )}
              {p.metodo === 'transferencia' && (
                <Input value={p.referencia} onChange={(e) => set(i, { referencia: e.target.value })} placeholder="Referencia" aria-label="Referencia de transferencia" className="h-8 w-36" />
              )}
            </div>
          </div>
        ))}
        {pagos.length < 4 && (
          <Button variante="secundario" tamano="sm" onClick={() => setPagos((l) => [...l, { metodo: 'transferencia', moneda: 'MXN', monto: '', referencia: '' }])}>
            <Plus /> Agregar otra forma de pago
          </Button>
        )}
      </fieldset>

      <fieldset className="rounded-lg border border-dashed border-border p-3">
        <legend className="flex items-center gap-1.5 px-1 text-[13px] font-medium">
          <HandCoins className="size-4 text-accent" /> Propina (opcional)
        </legend>
        <div className="flex gap-1.5">
          <Input type="number" min="0" step="0.01" value={propina.monto} onChange={(e) => setPropina({ ...propina, monto: e.target.value })} placeholder="0.00" aria-label="Monto de propina" className="cifras w-28" />
          <Select
            value={propina.barberoId}
            onChange={(e) => setPropina({ ...propina, barberoId: e.target.value })}
            aria-label="Para quién es la propina"
            aria-invalid={propinaSinDueno || undefined}
            className="flex-1"
          >
            {propina.barberoId === SIN_ASIGNAR && (
              <option value={SIN_ASIGNAR} disabled>
                Seleccionar trabajador
              </option>
            )}
            <option value="">Para todos (se reparte)</option>
            {barberos.data?.map((b) => (
              <option key={b.id} value={b.id}>
                Para {b.nombre}
              </option>
            ))}
          </Select>
        </div>
        {propinaSinDueno && (
          <p role="alert" className="mt-1.5 text-[12.5px] text-destructive">
            {AVISO_PROPINA}
          </p>
        )}
      </fieldset>

      <dl className="cifras grid grid-cols-2 gap-1 text-[14px]">
        <dt className="text-foreground-muted">Recibido</dt>
        <dd className="text-right">{formatearMoneda(r.pagadoMXN)}</dd>
        {r.faltaMXN > 0 && (
          <>
            <dt className="text-destructive">Falta</dt>
            <dd className="text-right font-semibold text-destructive">{formatearMoneda(r.faltaMXN)}</dd>
          </>
        )}
        {r.cambioMXN > 0 && (
          <>
            <dt className="font-medium">Cambio</dt>
            <dd className="text-right font-display text-[22px]">{formatearMoneda(r.cambioMXN)}</dd>
          </>
        )}
      </dl>
      {!r.valido && entrada.length > 0 && <p className="text-[13px] text-destructive">Tarjeta y transferencia no pueden dar cambio: ajusta los montos.</p>}
      {ventaEnCeroInvalida && <p className="text-[13px] text-destructive">Esta venta tiene servicios sin precio. Configura el precio antes de cobrar.</p>}

      {filaTarjeta ? (
        <Button tamano="lg" disabled={!listo || bloqueado || !enLinea} cargando={enviarTarjeta.isPending} onClick={siPropinaAsignada(() => enviarTarjeta.mutate())}>
          <CreditCard /> Cobrar y enviar a terminal
        </Button>
      ) : (
        <Button tamano="lg" disabled={!listo || bloqueado} cargando={cobrar.isPending} onClick={siPropinaAsignada(() => cobrar.mutate())}>
          {totalACobrar === 0 && esCortesia ? <HandCoins /> : entrada.some((p) => p.metodo === 'transferencia') ? <Landmark /> : <Banknote />}
          {totalACobrar === 0 && esCortesia ? 'Finalizar cortesía' : totalACobrar === 0 ? 'Completar cobro' : `Cobrar ${formatearMoneda(totalACobrar)}`}
        </Button>
      )}

      {(enviarTarjeta.isPending || esperando) && (
        <EsperaTerminalDialog
          ventaId={venta.id}
          cobroId={cobroEsperadoId}
          cobro={enviarTarjeta.isPending ? null : (cobroTarjeta.data ?? null)}
          envio={{
            alias: listaTerminales.find((t) => t.id === filaTarjeta?.terminalId)?.alias ?? 'la terminal',
            montoMXN: aCentavos(filaTarjeta?.monto ?? ''),
          }}
          onAprobado={alAprobar}
          onCerrar={alCerrarEspera}
        />
      )}
    </div>
  );
}
